import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowLeft, Library, Loader2, Lock, Sparkles, PenLine, BookText, Check, Users, Globe, ScrollText, Plus,
} from "lucide-react";
import { api, apiError, imageUrl } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const StatusBadge = ({ active }) => (
  <span className={`inline-flex items-center gap-1 font-ui text-[10px] uppercase tracking-widest font-semibold px-2.5 py-1 rounded-full ${
    active ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/40"
           : "bg-gold-400/10 text-slate-300 border border-gold-400/40"}`}>
    {active ? <><Check className="w-3 h-3" /> Active</> : <><Lock className="w-3 h-3" /> Locked · $19</>}
  </span>
);

const BibleBlock = ({ icon: Icon, label, text, empty }) => (
  <div className="rounded-xl border border-gold-500/12 bg-navy-950/50 p-4">
    <div className="flex items-center gap-1.5 font-ui text-[11px] uppercase tracking-widest text-slate-400/80 mb-2">
      <Icon className="w-3.5 h-3.5" /> {label}
    </div>
    <p className="font-read text-sm text-slate-300 leading-relaxed whitespace-pre-line">{text || <span className="text-slate-500 italic">{empty}</span>}</p>
  </div>
);

export const SeriesPage = ({ seriesList, activeSeriesId, onSelectSeries, onBack, onNewSeries, onBuySeries, onOpenBook, onWriteNext, onRefresh }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: "", premise: "", characters: "", setting: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (activeSeriesId) {
      setLoading(true);
      setEditing(false);
      api.getSeries(activeSeriesId).then((d) => {
        setDetail(d);
        setForm({ name: d.name, premise: d.premise, characters: d.characters, setting: d.setting });
      }).catch(() => toast.error("Could not load series")).finally(() => setLoading(false));
    } else {
      setDetail(null);
    }
  }, [activeSeriesId]);

  const save = async () => {
    setSaving(true);
    try {
      const updated = await api.updateSeries(activeSeriesId, form);
      setDetail((d) => ({ ...d, ...updated }));
      setEditing(false);
      toast.success("Series bible updated");
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(apiError(e, "Could not save."));
    } finally {
      setSaving(false);
    }
  };

  // ---------- LIST VIEW ----------
  if (!activeSeriesId) {
    return (
      <div data-testid="series-list-page" className="max-w-5xl mx-auto py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-4xl text-slate-100 flex items-center gap-3">
              <Library className="w-8 h-8 text-slate-300" /> Your series
            </h1>
            <p className="font-read text-slate-400 mt-2">Continuity engines that keep characters &amp; worlds consistent across books.</p>
          </div>
          <button data-testid="series-list-new" onClick={onNewSeries}
            className="klyr-btn-metal font-ui text-sm font-semibold rounded-full px-5 py-2.5 inline-flex items-center gap-2 transition-[filter]">
            <Plus className="w-4 h-4" /> New series
          </button>
        </div>

        {seriesList.length === 0 ? (
          <div data-testid="series-empty" className="rounded-2xl border border-dashed border-gold-500/12 p-16 text-center">
            <Library className="w-10 h-10 text-stone-700 mx-auto mb-4" />
            <p className="font-read text-slate-400">No series yet. Start one to reuse a cast and world across a set of books.</p>
            <button onClick={onNewSeries} className="mt-5 klyr-btn-metal font-ui text-sm font-semibold rounded-full px-6 py-2.5 transition-[filter]">
              Create your first series
            </button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {seriesList.map((s) => (
              <motion.button key={s.id} whileHover={{ y: -4 }}
                data-testid={`series-card-${s.id}`}
                onClick={() => (s.status === "active" ? onSelectSeries(s.id) : onBuySeries(s))}
                className="text-left rounded-2xl border border-gold-500/12 bg-navy-900/40 p-5 hover:border-gold-400/50 transition-colors">
                <div className="flex items-start justify-between gap-2 mb-3">
                  <BookText className="w-6 h-6 text-slate-300" />
                  <StatusBadge active={s.status === "active"} />
                </div>
                <h3 className="font-ui text-lg font-semibold text-slate-100 leading-tight">{s.name}</h3>
                <p className="font-read text-sm text-slate-500 mt-2 line-clamp-2">{s.premise || "No premise yet."}</p>
                <div className="font-ui text-xs text-slate-500 mt-4">
                  {s.status === "active" ? `${(s.book_ids || []).length} book${(s.book_ids || []).length === 1 ? "" : "s"}` : "Tap to unlock"}
                </div>
              </motion.button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ---------- DETAIL VIEW ----------
  if (loading || !detail) {
    return <div className="min-h-[50vh] flex items-center justify-center"><Loader2 className="w-8 h-8 text-slate-400 animate-spin" /></div>;
  }

  const active = detail.status === "active";

  return (
    <div data-testid="series-dashboard" className="max-w-5xl mx-auto py-8">
      <button data-testid="series-dashboard-back" onClick={onBack}
        className="flex items-center gap-2 font-ui text-sm text-slate-400 hover:text-gold-200 transition-colors mb-6">
        <ArrowLeft className="w-4 h-4" /> All series
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <StatusBadge active={active} />
          <h1 className="font-display text-4xl text-slate-100 mt-3">{detail.name}</h1>
        </div>
        <div className="flex items-center gap-3">
          {!active && (
            <button data-testid="series-dashboard-unlock" onClick={() => onBuySeries(detail)}
              className="klyr-btn-metal font-ui text-sm font-semibold rounded-full px-5 py-2.5 transition-[filter]">
              Unlock — $19
            </button>
          )}
          {active && (
            <button data-testid="series-write-next" onClick={() => onWriteNext(detail.id)}
              className="klyr-btn-metal font-ui text-sm font-semibold rounded-full px-5 py-2.5 inline-flex items-center gap-2 transition-[filter]">
              <PenLine className="w-4 h-4" /> Write the next book
            </button>
          )}
        </div>
      </div>

      {/* Shared bible */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-ui text-lg font-semibold text-slate-100 flex items-center gap-2">
            <ScrollText className="w-5 h-5 text-slate-300" /> Series bible
          </h2>
          {!editing ? (
            <button data-testid="series-edit-bible" onClick={() => setEditing(true)}
              className="font-ui text-sm text-slate-300 hover:text-gold-200 transition-colors">Edit</button>
          ) : (
            <div className="flex items-center gap-2">
              <button onClick={() => { setEditing(false); setForm({ name: detail.name, premise: detail.premise, characters: detail.characters, setting: detail.setting }); }}
                className="font-ui text-sm text-slate-400 hover:text-slate-200">Cancel</button>
              <button data-testid="series-save-bible" onClick={save} disabled={saving}
                className="klyr-btn-metal font-ui text-sm font-semibold rounded-lg px-4 py-1.5 inline-flex items-center gap-1.5 transition-[filter]">
                {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save
              </button>
            </div>
          )}
        </div>

        {!editing ? (
          <div className="grid md:grid-cols-3 gap-4">
            <BibleBlock icon={Sparkles} label="Premise" text={detail.premise} empty="No premise set." />
            <BibleBlock icon={Users} label="Recurring characters" text={detail.characters} empty="No characters set." />
            <BibleBlock icon={Globe} label="World & settings" text={detail.setting} empty="No world details set." />
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { k: "premise", label: "Premise", ph: "The overarching story or theme." },
              { k: "characters", label: "Recurring characters", ph: "Names, traits, appearances to keep consistent." },
              { k: "setting", label: "World & settings", ph: "Locations, rules and tone." },
            ].map((f) => (
              <div key={f.k}>
                <label className="font-ui text-[11px] uppercase tracking-widest text-slate-400/80">{f.label}</label>
                <Textarea data-testid={`series-edit-${f.k}`} value={form[f.k]} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                  placeholder={f.ph} className="mt-1.5 min-h-[120px] resize-none bg-navy-950/60 border-gold-500/12 font-read text-slate-100" />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Books in series */}
      <h2 className="font-ui text-lg font-semibold text-slate-100 flex items-center gap-2 mb-4">
        <BookText className="w-5 h-5 text-slate-300" /> Books in this series
        <span className="font-ui text-sm text-slate-500">({(detail.books || []).length})</span>
      </h2>
      {(detail.books || []).length === 0 ? (
        <div data-testid="series-books-empty" className="rounded-2xl border border-dashed border-gold-500/12 p-12 text-center">
          <p className="font-read text-slate-400">No books yet.{active ? " Write the first one to start the series." : ""}</p>
          {active && (
            <button onClick={() => onWriteNext(detail.id)} className="mt-4 klyr-btn-metal font-ui text-sm font-semibold rounded-full px-6 py-2.5 transition-[filter]">
              Write book one
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {detail.books.map((b, i) => (
            <button key={b.id} data-testid={`series-book-${b.id}`} onClick={() => onOpenBook(b.id)}
              className="group text-left">
              <div className="aspect-[2/3] rounded-lg overflow-hidden border border-gold-500/12 group-hover:border-gold-400/60 bg-navy-900 transition-colors relative">
                {b.cover_image_id ? (
                  <img src={imageUrl(b.cover_image_id)} alt={b.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><BookText className="w-8 h-8 text-stone-700" /></div>
                )}
                <div className="absolute top-2 left-2 font-ui text-[10px] font-bold bg-navy-950/80 text-slate-200 rounded px-1.5 py-0.5">#{i + 1}</div>
                {b.status !== "done" && (
                  <div className="absolute inset-0 bg-navy-950/60 flex items-center justify-center">
                    <span className="font-ui text-[10px] uppercase tracking-widest text-slate-300">{b.status}</span>
                  </div>
                )}
              </div>
              <div className="font-ui text-sm text-slate-200 mt-2 line-clamp-2 group-hover:text-gold-200 transition-colors">{b.title || "Untitled"}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
